import React from 'react'
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { EU_AUTHORITY_NAME } from '@constants/legal'
import { useNestedTranslation, useShouldDisplayProviderScopes, usePermissionsString } from '@hooks'
import { DefaultPageLayout } from '@layouts'
import { isOnlyInfoScope } from '@utils/scope'
import useConsent, { useDataUseWithFallback } from '../../hooks'
import ConsentButtons from '../ConsentButtons'
import ConsentDataAccessNote from '../ConsentDataAccessNote'
import ConsentDataUseAccessNote from '../ConsentDataUseAccessNote'
import ConsentLayoutUKEU from '../ConsentLayoutUKEU'
import ConsentLegalNote from '../ConsentLegalNote'
import ConsentNotesAccordionUKEU from '../ConsentNotesAccordionUKEU'
import ConsentNotesLink from '../ConsentNotesLink'
import ConsentNotesTabsUKEU from '../ConsentNotesTabsUKEU'
import ConsentOngoingAccessNote from '../ConsentOngoingAccessNote'
import {
  ConsentPermissionsListNote,
  ConsentProviderPermissionsListNote,
  ConsentLegalNoteContent,
} from '@components'
import ConsentLogosShieldCheck from '../ConsentLogosShieldCheck'

function ConsentContentEURegulated() {
  const titleTranslation = useNestedTranslation('v2_consent', ['title'])
  const whatDataAmISharingTranslation = useNestedTranslation('v2_consent', [
    'what_data_am_i_sharing',
  ])
  const howDoesThisWorkTranslation = useNestedTranslation('v2_consent', ['how_does_this_work'])
  const subtitleTranslation = useNestedTranslation('v2_consent', ['subtitle'])

  const {
    providerName,
    clientName,
    regulatoryInformation,
    scope,
    shouldDisplayNewSubtitleCopy,
    providerScope,
  } = useConsent()

  const { dataUseCopy, dataUseBase } = useDataUseWithFallback()

  const title = isOnlyInfoScope(scope)
    ? titleTranslation('access_info_scope', {
        clientName,
        providerName,
      })
    : titleTranslation('access', {
        clientName,
      })

  const subtitle = shouldDisplayNewSubtitleCopy
    ? subtitleTranslation('retrieve_data_regulated_with_data_use', {
        clientName,
        dataUse: dataUseBase,
      })
    : undefined
  const shouldDisplayProviderScopes = useShouldDisplayProviderScopes({ scope, providerScope })
  const permissionsString = usePermissionsString({ scope, providerName })

  const notesProps = {
    howDoesThisWork: (
      <>
        <div data-view="how-does-this-work-explanation">
          {howDoesThisWorkTranslation('explanation.regulated', {
            providerName,
            clientName,
          })}
          .
        </div>
        <br />
        <div data-view="regulation-information">
          <div>
            {howDoesThisWorkTranslation('encryption_info.account', {
              entityName: clientName,
            })}
            .
          </div>
          <br />
          <div>{howDoesThisWorkTranslation('login_info', { clientName })}.</div>
          <br />
          <div>
            {howDoesThisWorkTranslation('authority_info', {
              entityName: clientName,
              authorityName: EU_AUTHORITY_NAME,
            })}
            .{' '}
            <ConsentNotesLink href={regulatoryInformation!.registration_page_url}>
              RN {regulatoryInformation!.registration_number}
            </ConsentNotesLink>
          </div>
        </div>
      </>
    ),
    whatDataAmISharing: (
      <>
        <ConsentDataAccessNote />
        <br />
        <ConsentPermissionsListNote scope={scope} />
        <br />
        <ConsentOngoingAccessNote />
        {shouldDisplayProviderScopes && (
          <>
            <div>
              {whatDataAmISharingTranslation('info.access_provider', {
                providerName,
                clientName,
              })}
              :
            </div>
            <br />
            <ConsentProviderPermissionsListNote scope={scope} providerScope={providerScope} />
            <br />
            <div data-view="sharing-disclaimer">
              {whatDataAmISharingTranslation('disclaimer.data_not_used_regulated', {
                clientName,
                permissionsString,
              })}
              .
            </div>
          </>
        )}
      </>
    ),
    howIsMyDataUsed: (
      <>
        {dataUseCopy}
        .
        <br />
        <br />
        <ConsentDataUseAccessNote />
      </>
    ),
  }

  return (
    <DefaultPageLayout title={title} subtitle={subtitle} logo={<ConsentLogosShieldCheck />}>
      <ConsentLayoutUKEU
        notesAccordion={<ConsentNotesAccordionUKEU {...notesProps} />}
        notesTabs={<ConsentNotesTabsUKEU {...notesProps} />}
        agreement={
          <>
            <ConsentButtons />
            <ConsentLegalNote>
              <ConsentLegalNoteContent
                entityName={clientName}
                tosLink={regulatoryInformation.terms_of_service_url!}
                privacyLink={regulatoryInformation.privacy_policy_url!}
              />
            </ConsentLegalNote>
          </>
        }
      />
    </DefaultPageLayout>
  )
}

export default ConsentContentEURegulated
